import { useEffect, useState } from "react";
import { Command } from "cmdk";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  CreditCard,
  Cpu,
  FileText,
  Home,
  Languages,
  LayoutGrid,
  Moon,
  Package,
  Search as SearchIcon,
  Settings,
} from "lucide-react";
import { useTheme } from "@/shared/theme/ThemeProvider";

/**
 * Web Command palette (Web Hi-Fi v1 — Cmd+K modal) + keyboard
 * shortcuts dialog. Triggered globally via Cmd/Ctrl+K and Cmd/Ctrl+/.
 *
 * Built on `cmdk` (Radix-based) so fuzzy-search + keyboard nav (arrows,
 * enter, esc) come for free. Items wire to react-router navigate + a
 * few actions (toggle theme).
 *
 * Mount once at the app root. Stateless w.r.t. session; routes that
 * require auth will redirect via the route guard if you punch them in
 * pre-login.
 *
 * Colours: semantic token classes (bg-elevated / text-fg / border-*),
 * including the compact keyboard-hint chips (ADR-011 / DS-3b — no
 * CSS-var inline styles remain).
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
      }
    };
    // Custom event bridge — lets the Header's Cmd+K button (and any
    // future caller) open this palette without prop-drilling state up
    // a level. Same pattern as the `isale:session-cleared` event in
    // shared/session.ts. C2 from iSale-User-Flow-Index.md §6 audit.
    const onOpenRequest = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("isale:cmd-palette-open", onOpenRequest);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("isale:cmd-palette-open", onOpenRequest);
    };
  }, []);

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <>
      {/* Cmd+K palette */}
      <Command.Dialog
        open={open}
        onOpenChange={setOpen}
        label="Command palette"
        className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]" /* layout-ok: command overlay stacking + viewport offset */
      >
        {/* Visually-hidden title for screen readers. cmdk's underlying
            Radix Dialog emits a dev-only a11y warning when no Dialog.Title
            child exists; we can't use `<Dialog.Title>` directly here
            because Command.Dialog's internal DialogContext isn't reachable
            via the public re-export. The plain `<h2>` satisfies SR users
            (announced as the dialog name) and the dev warning is a
            non-blocking nit. */}
        <h2
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            margin: -1,
            padding: 0,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          Command palette
        </h2>
        {/* Backdrop */}
        <div
          aria-hidden
          onClick={() => setOpen(false)}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        />
        {/* Modal */}
        <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-hero border border-default bg-elevated shadow-float">
          <div className="flex items-center gap-2 border-b border-subtle px-4 py-3">
            <SearchIcon className="h-4 w-4 text-fg-subtle" />
            <Command.Input
              placeholder="Tìm route, action…"
              className="flex-1 bg-transparent text-sm text-fg outline-none"
            />
            <kbd className="rounded border border-default bg-surface px-1.5 py-0.5 text-3xs font-bold text-fg-subtle">
              ESC
            </kbd>
          </div>
          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="px-4 py-6 text-center text-sm text-fg-subtle">
              Không có kết quả.
            </Command.Empty>

            <Command.Group heading="Đi đến" className="text-xs">
              <Item onSelect={() => go("/")} icon={Home} label="Trang chủ" hint="G H" />
              <Item onSelect={() => go("/products")} icon={Package} label="Sản phẩm" hint="G P" />
              <Item onSelect={() => go("/projects")} icon={LayoutGrid} label="Dự án" hint="G J" />
              <Item onSelect={() => go("/settings/billing")} icon={CreditCard} label="Quota & gói" hint="G B" />
              <Item onSelect={() => go("/settings/ai-activity")} icon={Cpu} label="Nhật ký AI" hint="G A" />
              <Item onSelect={() => go("/settings")} icon={Settings} label="Cài đặt" hint="G S" />
              <Item onSelect={() => go("/settings/theme")} icon={Moon} label="Theme" />
              <Item onSelect={() => go("/settings/language")} icon={Languages} label="Ngôn ngữ" />
            </Command.Group>

            <Command.Group heading="Hành động" className="text-xs">
              <Item
                onSelect={() => setMode(mode === "dark" ? "light" : "dark")}
                icon={Moon}
                label={mode === "dark" ? "Chuyển sang Light" : "Chuyển sang Dark"}
              />
              <Item
                onSelect={() => {
                  setOpen(false);
                  setShortcutsOpen(true);
                }}
                icon={FileText}
                label="Phím tắt"
                hint="⌘/"
              />
            </Command.Group>
          </Command.List>
        </div>
      </Command.Dialog>

      {/* Shortcuts help dialog */}
      {shortcutsOpen && (
        <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />
      )}
    </>
  );
}

function Item({
  onSelect,
  icon: Icon,
  label,
  hint,
}: {
  onSelect: () => void;
  icon: typeof Home;
  label: string;
  hint?: string;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-fg aria-selected:bg-primary-soft"
    >
      <Icon className="h-4 w-4 text-fg-muted" />
      <span className="flex-1">{label}</span>
      {hint && (
        <kbd className="rounded border border-default px-1.5 py-0.5 text-3xs font-bold text-fg-subtle">
          {hint}
        </kbd>
      )}
    </Command.Item>
  );
}

function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-label="Phím tắt"
      className="fixed inset-0 z-[100] flex items-center justify-center" /* layout-ok: command overlay stacking */
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-hero border border-default bg-elevated shadow-float">
        <div className="border-b border-subtle px-5 py-3">
          <h2 className="text-base font-extrabold text-fg">Phím tắt</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">ESC để đóng</p>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 p-5 sm:grid-cols-2">
          <ShortcutRow label="Mở Command palette" keys="⌘ K" />
          <ShortcutRow label="Mở phím tắt" keys="⌘ /" />
          <ShortcutRow label="Trang chủ" keys="G H" />
          <ShortcutRow label="Sản phẩm" keys="G P" />
          <ShortcutRow label="Dự án" keys="G J" />
          <ShortcutRow label="Quota / Billing" keys="G B" />
          <ShortcutRow label="AI activity" keys="G A" />
          <ShortcutRow label="Settings" keys="G S" />
        </div>
      </div>
    </div>
  );
}

function ShortcutRow({ label, keys }: { label: string; keys: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm text-fg">{label}</span>
      <kbd className="rounded border border-default bg-surface px-1.5 py-0.5 text-2xs font-bold text-fg-muted">
        {keys}
      </kbd>
    </div>
  );
}
